import { useEffect, useState } from "react";
import { CalendarOff, Loader2, Plus, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Num } from "./fields";

export default function BookingRules() {
  const [st, setSt] = useState(null);
  const [saved, setSaved] = useState("");
  const [newDate, setNewDate] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/booking-settings").then((r) => { setSt(r.data); setSaved(JSON.stringify(r.data)); }).catch((e) => toast.error(formatErr(e))); }, []);
  if (!st) return <Loader2 className="w-5 h-5 animate-spin text-brand-accent" />;

  const dirty = JSON.stringify(st) !== saved;
  const add = () => { if (newDate && !st.blocked_dates.includes(newDate)) setSt({ ...st, blocked_dates: [...st.blocked_dates, newDate].sort() }); setNewDate(""); };
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/booking-settings", st); setSt(data); setSaved(JSON.stringify(data)); toast.success("Booking rules saved"); }
    catch (e) { toast.error(formatErr(e)); }
    setSaving(false);
  };

  return (
    <div className="rounded-2xl border border-brand-line bg-brand-sand p-5 mb-6" data-testid="booking-rules">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex-1 min-w-[260px]">
          <p className="text-xs uppercase tracking-[0.2em] text-brand-accent inline-flex items-center gap-2"><CalendarOff className="w-3.5 h-3.5" /> Availability rules</p>
          <p className="text-sm text-brand-muted mt-2">Clients can only pick open windows. Cancelled bookings free up their slot.</p>
          <label className="block mt-4 text-[10px] uppercase tracking-[0.15em] text-brand-muted-light w-56">Max visits per time window / day
            <Num value={st.cap_per_window} onChange={(v) => setSt({ ...st, cap_per_window: v === "" ? "" : Math.max(1, v) })} step="1" min="1" testid="booking-cap-input" /></label>
        </div>
        <div className="flex-1 min-w-[280px]">
          <p className="text-[10px] uppercase tracking-[0.15em] text-brand-muted-light">Blocked dates (holidays, no visits)</p>
          <div className="flex gap-2 mt-2">
            <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="rounded-md border border-brand-line bg-white px-2.5 py-1.5 text-sm outline-none focus:border-brand-accent" data-testid="blocked-date-input" />
            <button onClick={add} className="inline-flex items-center gap-1 rounded-full bg-brand-ink text-brand-cream text-sm px-4 py-1.5" data-testid="add-blocked-date-btn"><Plus className="w-4 h-4" /> Block</button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {st.blocked_dates.length === 0 && <span className="text-xs text-brand-muted" data-testid="blocked-dates-empty">No blocked dates</span>}
            {st.blocked_dates.map((d) => (
              <span key={d} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-line px-3 py-1 text-xs font-mono-num" data-testid={`blocked-date-${d}`}>
                {d}<button onClick={() => setSt({ ...st, blocked_dates: st.blocked_dates.filter((x) => x !== d) })} className="text-brand-muted-light hover:text-red-700" data-testid={`remove-blocked-date-${d}`} aria-label="Remove"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <button onClick={save} disabled={!dirty || saving} data-testid="booking-rules-save-btn"
          className="inline-flex items-center gap-2 rounded-full bg-brand-accent hover:bg-brand-accent-dark disabled:opacity-50 text-white px-5 py-2 text-sm transition-colors">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save rules
        </button>
      </div>
    </div>
  );
}
